// `full` вшивает перехват исключений и запись сессий в бандл: обычная сборка
// догружает их отдельными скриптами, и ошибки первого рендера пролетают
// (design.md Д4).
import posthog from "posthog-js/full";

/**
 * Аналитика веб-клиента в PostHog Cloud EU (§103, stage9-posthog-analytics).
 * Демон в PostHog ничего не шлёт — только этот модуль.
 */

/** Хост по умолчанию — EU: забытая переменная не уводит данные в US Cloud (Д3а). */
export const DEFAULT_POSTHOG_HOST = "https://eu.i.posthog.com";

/** Хост приёма: заданный при сборке или EU по умолчанию (Д3а). */
export function resolvePosthogHost(host: string | undefined): string {
  return host === undefined || host === "" ? DEFAULT_POSTHOG_HOST : host;
}

/** Состояние сессии клиента — то, что определяет имя экрана в просмотре. */
export type PageviewStatus = "authenticated" | "anonymous" | "checking" | "unavailable";

/** Экраны сброса пароля: рисуются поверх любой сессии (stage9-password-reset). */
export type PageviewAuthRoute = "forgot" | "reset";

/**
 * Имя экрана для свойства `screen` просмотра (Д6): при `authenticated` —
 * экран и, для плотностей, вид (`densities/cards`); иначе — что рисуется
 * вместо экрана: `login`, `checking`, `unavailable`. Экраны сброса пароля
 * называются отдельно: `#/reset` (`password_reset`) рисуется при любой сессии,
 * `#/forgot` (`password_reset_request`) — у вошедшего и вышедшего; пока сессия
 * проверяется или демон недоступен, виден заглушечный экран. Токен в имя не входит.
 */
export function pageviewScreenName(
  status: PageviewStatus,
  screen: string,
  densityView: string | null,
  authRoute: PageviewAuthRoute | null = null,
): string {
  if (authRoute === "reset") {
    return "password_reset";
  }
  if (authRoute === "forgot" && (status === "authenticated" || status === "anonymous")) {
    return "password_reset_request";
  }
  switch (status) {
    case "authenticated":
      return screen === "densities" && densityView !== null ? `densities/${densityView}` : screen;
    case "anonymous":
      return "login";
    case "checking":
      return "checking";
    case "unavailable":
      return "unavailable";
  }
}

/** Исходные данные решения «слать ли аналитику». */
export type AnalyticsEnv = {
  /** Боевая сборка (`import.meta.env.PROD`). */
  prod: boolean;
  /** Ключ проекта PostHog; пустой или отсутствующий — аналитики нет (§103 п. 4). */
  key: string | undefined;
  /** Адрес, на котором открыта страница. */
  hostname: string;
};

/** Локальные адреса: браузерные тесты и локальный демон в PostHog не попадают (§103 п. 5). */
const LOCAL_HOSTNAMES: ReadonlySet<string> = new Set(["localhost", "127.0.0.1"]);

/**
 * Включена ли аналитика: боевая сборка, ключ задан, адрес не локальный
 * (§103 пп. 4–5, design.md Д2). Чистая функция — проверяется без браузера.
 */
export function analyticsEnabled({ prod, key, hostname }: AnalyticsEnv): boolean {
  return prod && key !== undefined && key !== "" && !LOCAL_HOSTNAMES.has(hostname);
}

/**
 * Сегмент токена в адресе регистрации и подтверждения почты:
 * `#/signup/{token}` → `#/signup`, `#/confirm/{token}` → `#/confirm`,
 * `#/reset/{token}` → `#/reset`.
 */
const SIGNUP_TOKEN_IN_URL = /(#\/(?:signup|confirm|reset))\/[^\s"'?&#]*/g;

/** Глубже адресные свойства PostHog не лежат; предел защищает от обхода записи сессии. */
const SCRUB_MAX_DEPTH = 6;

function scrubValue(value: unknown, depth: number): unknown {
  if (typeof value === "string") {
    return value.replace(SIGNUP_TOKEN_IN_URL, "$1");
  }
  if (depth >= SCRUB_MAX_DEPTH || typeof value !== "object" || value === null) {
    return value;
  }
  if (Array.isArray(value)) {
    return value.map((item) => scrubValue(item, depth + 1));
  }
  // Пересобираются только простые объекты: `Date` в `timestamp` события
  // превращался в `{}`, и приём PostHog отвечал 400 на весь пакет.
  const proto: unknown = Object.getPrototypeOf(value);
  if (proto !== Object.prototype && proto !== null) {
    return value;
  }
  return Object.fromEntries(
    Object.entries(value).map(([key, item]) => [key, scrubValue(item, depth + 1)]),
  );
}

/**
 * `before_send`: вырезает токен ссылки-приглашения из любого адресного
 * свойства события (`$current_url`, `$referrer`, `$set_once` и прочих)
 * (stage9-signup-invites, design.md Д9). Основная защита — токен убирается из
 * адреса до `initAnalytics` (`signup-route.ts`); это страховка на случай, если
 * порядок нарушит будущая правка. Объект не мутируется.
 */
export function scrubSignupToken<T>(event: T): T {
  return scrubValue(event, 0) as T;
}

/** Решение, принятое в `initAnalytics`; до вызова и после отказа — `false`. */
let enabled = false;

/**
 * Запускает PostHog, если аналитика включена (design.md Д3, Д4). Вызывается
 * один раз до рендера, чтобы ошибки первого рендера уже ловились.
 */
export function initAnalytics(): void {
  const key: string | undefined = import.meta.env.VITE_POSTHOG_KEY;
  const host: string | undefined = import.meta.env.VITE_POSTHOG_HOST;
  if (
    !key ||
    !analyticsEnabled({
      prod: import.meta.env.PROD,
      key,
      hostname: window.location.hostname,
    })
  ) {
    return;
  }
  posthog.init(key, {
    api_host: resolvePosthogHost(host),
    // Анонимный заход до входа не плодит профилей (Д3).
    person_profiles: "identified_only",
    // Экран меняется по hash, а автопросмотр PostHog ловит только загрузку
    // и pushState — просмотры шлёт `capturePageview` (Д6).
    capture_pageview: false,
    // Необработанные исключения и отклонённые промисы (§103 п. 1).
    capture_exceptions: true,
    // Поля ввода, включая пароль, маскируются; текст рынка — нет (§103 п. 3).
    session_recording: { maskAllInputs: true },
    // Токен ссылки-приглашения в адрес события не попадает (Д9 stage9-signup-invites).
    before_send: scrubSignupToken,
  });
  enabled = true;
}

/** Называет пользователя его логином (§103 п. 2, §27). */
export function identifyUser(login: string): void {
  if (enabled) {
    posthog.identify(login);
  }
}

/** Сбрасывает идентичность при выходе и истечении сессии (§103 п. 2). */
export function resetUser(): void {
  if (enabled) {
    posthog.reset();
  }
}

/** Просмотр экрана с именем экрана в свойстве `screen`; вызывается при смене маршрута (Д6). */
export function capturePageview(screenName: string): void {
  if (enabled) {
    posthog.capture("$pageview", { screen: screenName });
  }
}

/**
 * Флаг фичи. Выключенная аналитика, недоступный PostHog и ещё не
 * полученные флаги (`undefined`) читаются как «выключен» (§103 п. 6, Д7).
 */
export function isFeatureEnabled(flag: string): boolean {
  return enabled && posthog.isFeatureEnabled(flag) === true;
}
